import { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, FormField, FormSelect } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { dollarsToCents } from "@/lib/money";
import { usePrograms, useTerms, useCreateClassSection } from "@/api/hooks/useClassRegistration";
import {
  AGE_BAND_LABELS,
  CLASS_SLOT_LABELS,
  WEEKDAY_LABELS,
  type AgeBand,
  type ClassKind,
  type ClassSlot,
  type Weekday,
} from "@/api/types/classRegistration";

interface SectionFormDialogProps {
  open: boolean;
  onClose: () => void;
  defaultTermId?: string;
  onCreated?: (sectionId: string) => void;
}

const PROGRAM_WEEKDAYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri"];
const PROGRAM_SLOTS: ClassSlot[] = ["morning_9_11", "midday_12_2", "afternoon_4_6"];
const AGE_BANDS: AgeBand[] = ["age_4_6", "age_7_9", "age_10_12"];

/**
 * Create-section form. A section is either a `program_class` (program +
 * age band + one weekday Mon-Fri + one of the three daytime slots) or the
 * `lunch_extension` (no program, no age band, fixed weekday=`tue_thu` /
 * slot=`lunch_11_12` — see app/models/class_section.py's
 * `chk_class_section_kind_shape` CHECK constraint, which this form's
 * conditional fields exist purely to satisfy client-side before the
 * request ever reaches that constraint).
 */
export function SectionFormDialog({ open, onClose, defaultTermId, onCreated }: SectionFormDialogProps) {
  const { data: terms } = useTerms(false);
  const { data: programs } = usePrograms(true);
  const createSection = useCreateClassSection();

  const [termId, setTermId] = useState(defaultTermId ?? "");
  const [kind, setKind] = useState<ClassKind>("program_class");
  const [programId, setProgramId] = useState("");
  const [ageBand, setAgeBand] = useState<AgeBand | "">("");
  const [weekday, setWeekday] = useState<Weekday>("mon");
  const [slot, setSlot] = useState<ClassSlot>("morning_9_11");
  const [priceDollars, setPriceDollars] = useState("");
  const [cap, setCap] = useState("12");
  const [minEnrollment, setMinEnrollment] = useState("4");
  const [instructorName, setInstructorName] = useState("");
  const [instructorContact, setInstructorContact] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTermId(defaultTermId ?? "");
      setKind("program_class");
      setProgramId("");
      setAgeBand("");
      setWeekday("mon");
      setSlot("morning_9_11");
      setPriceDollars("");
      setCap("12");
      setMinEnrollment("4");
      setInstructorName("");
      setInstructorContact("");
      setServerError(null);
    }
  }, [open, defaultTermId]);

  const isLunchExtension = kind === "lunch_extension";

  const canSubmit = useMemo(() => {
    if (!termId || !priceDollars.trim() || createSection.isPending) return false;
    if (isLunchExtension) return true;
    return !!programId && !!ageBand;
  }, [termId, priceDollars, isLunchExtension, programId, ageBand, createSection.isPending]);

  async function handleSubmit() {
    if (!canSubmit) return;
    setServerError(null);
    try {
      const section = await createSection.mutateAsync({
        term_id: termId,
        kind,
        weekday: isLunchExtension ? "tue_thu" : weekday,
        slot: isLunchExtension ? "lunch_11_12" : slot,
        price_cents_monthly: dollarsToCents(priceDollars),
        program_id: isLunchExtension ? null : programId,
        age_band: isLunchExtension ? null : ageBand === "" ? null : ageBand,
        cap: cap.trim() ? Number(cap) : undefined,
        min_enrollment: minEnrollment.trim() ? Number(minEnrollment) : undefined,
        instructor_name: instructorName.trim() || null,
        instructor_contact: instructorContact.trim() || null,
      });
      toastSuccess("Section added");
      onCreated?.(section.id);
      onClose();
    } catch (err) {
      const message = getErrorMessage(err);
      setServerError(message);
      toastError("Couldn't add section", message);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New section">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>New section</DialogHeader>
        <DialogBody className="space-y-4">
          <FormSelect label="Term" required value={termId} onChange={(e) => setTermId(e.target.value)}>
            <option value="">Select a term…</option>
            {(terms ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </FormSelect>

          <FormSelect
            label="Kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as ClassKind)}
          >
            <option value="program_class">Class</option>
            <option value="lunch_extension">Lunch Extension</option>
          </FormSelect>

          {!isLunchExtension && (
            <>
              <FormSelect label="Program" required value={programId} onChange={(e) => setProgramId(e.target.value)}>
                <option value="">Select a program…</option>
                {(programs ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </FormSelect>

              <FormSelect
                label="Age band"
                required
                value={ageBand}
                onChange={(e) => setAgeBand(e.target.value as AgeBand)}
              >
                <option value="">Select an age band…</option>
                {AGE_BANDS.map((b) => (
                  <option key={b} value={b}>
                    {AGE_BAND_LABELS[b]}
                  </option>
                ))}
              </FormSelect>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormSelect label="Weekday" value={weekday} onChange={(e) => setWeekday(e.target.value as Weekday)}>
                  {PROGRAM_WEEKDAYS.map((w) => (
                    <option key={w} value={w}>
                      {WEEKDAY_LABELS[w]}
                    </option>
                  ))}
                </FormSelect>
                <FormSelect label="Time slot" value={slot} onChange={(e) => setSlot(e.target.value as ClassSlot)}>
                  {PROGRAM_SLOTS.map((s) => (
                    <option key={s} value={s}>
                      {CLASS_SLOT_LABELS[s]}
                    </option>
                  ))}
                </FormSelect>
              </div>
            </>
          )}

          {isLunchExtension && (
            <p className="text-xs text-text-muted">
              Runs {WEEKDAY_LABELS.tue_thu} · {CLASS_SLOT_LABELS.lunch_11_12}, open to any enrolled age.
            </p>
          )}

          <FormField
            label="Price per month ($)"
            required
            type="number"
            min={0}
            step="0.01"
            value={priceDollars}
            onChange={(e) => setPriceDollars(e.target.value)}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Capacity" type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} />
            <FormField
              label="Minimum to run"
              type="number"
              min={0}
              value={minEnrollment}
              onChange={(e) => setMinEnrollment(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Instructor name (optional)"
              value={instructorName}
              onChange={(e) => setInstructorName(e.target.value)}
            />
            <FormField
              label="Instructor contact (optional)"
              value={instructorContact}
              onChange={(e) => setInstructorContact(e.target.value)}
            />
          </div>

          {serverError && (
            <p className="text-sm text-danger" role="alert">
              {serverError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={createSection.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {createSection.isPending ? "Adding…" : "Add section"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
